import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { COST_LABELS, BENEFIT_TYPE_LABELS, BENEFIT_TYPE_DESCRIPTIONS, ROICostStructure, BenefitType } from '@/data/roiModelData';
import { Calculator } from 'lucide-react';

interface RoiHelpDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function RoiHelpDialog({ open, onOpenChange }: RoiHelpDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] p-0">
        <DialogHeader className="p-6 pb-3">
          <DialogTitle className="flex items-center gap-2">
            <Calculator className="w-5 h-5 text-emerald-600" />
            ¿Cómo funciona la calculadora de ROI?
          </DialogTitle>
          <DialogDescription>
            Sirve para estimar si el valor que genera tu solución justifica lo que cuesta. Son estimaciones:
            no necesitas cifras exactas ni contables, solo aproximaciones razonables y honestas.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh] px-6 pb-6">
          <div className="space-y-6 text-sm">
            <section className="space-y-3">
              <h4 className="font-semibold text-base">1. Costos (lo que invertiste)</h4>
              <p className="text-muted-foreground">Se suman los cuatro valores anuales que registres:</p>
              <ul className="space-y-2">
                {(Object.keys(COST_LABELS) as Array<keyof ROICostStructure>).map(key => (
                  <li key={key} className="p-3 rounded-lg bg-muted/50">
                    <p className="font-medium">{COST_LABELS[key].name}</p>
                    <p className="text-muted-foreground">{COST_LABELS[key].description}</p>
                  </li>
                ))}
              </ul>
              <p className="text-muted-foreground">
                Tip: para "Capital Humano" multiplica las horas dedicadas por el valor aproximado de la hora
                de quienes participaron.
              </p>
            </section>

            <section className="space-y-3">
              <h4 className="font-semibold text-base">2. Beneficios (lo que ganas al año)</h4>
              <ul className="space-y-2">
                {(Object.keys(BENEFIT_TYPE_LABELS) as BenefitType[]).map(bt => (
                  <li key={bt} className="p-3 rounded-lg bg-muted/50">
                    <p className="font-medium">{BENEFIT_TYPE_LABELS[bt]}</p>
                    <p className="text-muted-foreground">{BENEFIT_TYPE_DESCRIPTIONS[bt]}</p>
                  </li>
                ))}
              </ul>
              <p className="text-muted-foreground">
                Si tu beneficio es real pero no lo puedes traducir a dinero (mejor experiencia, menos estrés
                del equipo, mejor imagen), usa <strong>“No definido (intangible)”</strong>: cuenta como $0 en el
                cálculo financiero y es una respuesta perfectamente válida.
              </p>
            </section>

            <section className="space-y-3">
              <h4 className="font-semibold text-base">3. Fórmulas que usa el sistema</h4>
              <div className="p-4 rounded-lg bg-muted/50 font-mono text-xs space-y-2">
                <p>Costos totales = Suscripciones + Tokens + Capital humano + Capacitación</p>
                <p>Beneficios totales = suma de beneficios (los “intangibles” suman 0)</p>
                <p>ROI % = (Beneficios − Costos) ÷ Costos × 100</p>
                <p>BCR = Beneficios ÷ Costos</p>
                <p>Payback (meses) = Costos ÷ (Beneficios ÷ 12)</p>
              </div>
            </section>

            <section className="space-y-2">
              <h4 className="font-semibold text-base">4. Ejemplo</h4>
              <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-200 space-y-1">
                <p>Costos: suscripciones $1.200.000 + tokens $300.000 + capital humano $2.000.000 + capacitación $500.000 = <strong>$4.000.000</strong></p>
                <p>Beneficios: ahorro de tiempo (500 horas × $16.000) = <strong>$8.000.000</strong></p>
                <p>ROI = (8.000.000 − 4.000.000) ÷ 4.000.000 × 100 = <strong>100%</strong></p>
                <p>BCR = 8.000.000 ÷ 4.000.000 = <strong>2,0</strong> (ganas $2 por cada $1 invertido)</p>
                <p>Payback = 4.000.000 ÷ (8.000.000 ÷ 12) = <strong>6 meses</strong></p>
              </div>
            </section>

            <section className="space-y-2">
              <h4 className="font-semibold text-base">5. ¿Y si no tengo los números?</h4>
              <p className="text-muted-foreground">
                Estima con lo que sepas y explica tu supuesto en la descripción de cada beneficio
                (por ejemplo: “calculado con 2 horas ahorradas por semana”). Es mejor una estimación
                honesta y explicada que un número inventado.
              </p>
            </section>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

export default RoiHelpDialog;
